import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import i18n from '@/utils/i18n';
import {
  CanAccessUserData,
  ViewerContext,
} from '../../../../providers/ViewerContextProvider';
import { FaEdit, FaPlus } from 'react-icons/fa';
import LabeledCheckBox from '../../../../components/LabeledCheckBox';
import { useAddUserToEventAccountMutation } from '../../../../graphql/mutations/addUserToEventAccount.generated';
import { EventAccountUser } from '../../../../graphql/generatedTypes';
import { useUpdateEventAccountUserMutation } from '../../../../graphql/mutations/updateEventAccountUser.generated';
import { GetEventAccountDocument } from '../../../../graphql/queries/getEventAccount.generated';
import { isNullOrWhitespace } from '@/utils/stringHelper';

type EventAccountUserTableProps = {
  users: EventAccountUser[];
  loading: boolean;
  error: boolean;
  eventAccountId: string;
};

const EventAccountUserTable: React.FC<EventAccountUserTableProps> = ({
  eventAccountId,
  users,
  loading,
  error,
}) => {
  const viewer = useContext(ViewerContext);
  const [userInfoModalOpen, setUserInfoModalOpen] = React.useState(false);
  const [validationMessage, setValidationMessage] = React.useState('');

  const [selectedUser, setSelectedUser] = React.useState<EventAccountUser>({
    id: '',
    name: 'NewUser',
    password: Math.random().toString(36).slice(-8),
    can_do_order_fulfillments: false,
  });

  const [addUserToEventAccount] = useAddUserToEventAccountMutation();
  const [updateEventAccountUser] = useUpdateEventAccountUserMutation();

  const [action, setAction] = React.useState<'add' | 'edit'>('edit');
  async function addUser() {
    setAction('add');
    setSelectedUser({
      id: '',
      name: 'NewUser',
      password: Math.random().toString(36).slice(-8),
      can_do_order_fulfillments: false,
    });
    setUserInfoModalOpen(true);
  }

  function editUser(user: any) {
    setAction('edit');
    setSelectedUser(user);
    setUserInfoModalOpen(true);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    const isValid = /^[a-zA-Z0-9-_.@]*$/.test(value);

    if (!isValid) {
      setValidationMessage(i18n.t('EventAccount:userNameInvalidMessage'));
    } else {
      setValidationMessage('');
    }

    setSelectedUser({
      ...selectedUser,
      name: value,
    });
  }

  function userInfoModal() {
    return (
      <Modal
        isOpen={userInfoModalOpen}
        toggle={() => setUserInfoModalOpen(!userInfoModalOpen)}
      >
        <ModalHeader toggle={() => setUserInfoModalOpen(!userInfoModalOpen)}>
          {action === 'add' && i18n.t('EventAccount:addUser')}
          {action === 'edit' && i18n.t('EventAccount:editUser')}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="username">{i18n.t('EventAccount:username')}</Label>
            <Input
              type="text"
              name="username"
              id="username"
              value={selectedUser.name}
              onChange={handleInputChange}
              invalid={!isNullOrWhitespace(validationMessage)}
            />
            {validationMessage && (
              <FormFeedback>{validationMessage}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="password">{i18n.t('EventAccount:password')}</Label>
            <Input
              type="text"
              name="password"
              id="password"
              value={selectedUser.password}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, password: e.target.value })
              }
            />
          </FormGroup>
          <FormGroup>
            <LabeledCheckBox
              checked={selectedUser.can_do_order_fulfillments}
              onClick={() =>
                setSelectedUser({
                  ...selectedUser,
                  can_do_order_fulfillments:
                    !selectedUser.can_do_order_fulfillments,
                })
              }
            >
              {i18n.t('EventAccount:canDoOrderFulfillments')}
            </LabeledCheckBox>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={!isNullOrWhitespace(validationMessage)}
            onClick={() => {
              if (action === 'add') {
                addUserToEventAccount({
                  variables: {
                    event_account_id: eventAccountId,
                    input: {
                      user_name: selectedUser.name,
                      can_do_order_fulfillments:
                        selectedUser.can_do_order_fulfillments,
                      password: selectedUser.password,
                    },
                  },
                  refetchQueries: [
                    {
                      query: GetEventAccountDocument,
                      variables: { id: eventAccountId },
                    },
                  ],
                });
              } else {
                updateEventAccountUser({
                  variables: {
                    event_account_id: eventAccountId,
                    user_id: selectedUser.id,
                    input: {
                      user_name: selectedUser.name,
                      can_do_order_fulfillments:
                        selectedUser.can_do_order_fulfillments,
                      password: selectedUser.password,
                    },
                  },
                  refetchQueries: [
                    {
                      query: GetEventAccountDocument,
                      variables: { id: eventAccountId },
                    },
                  ],
                });
              }
              setUserInfoModalOpen(false);
            }}
          >
            {action === 'add' && i18n.t('Common:add')}
            {action === 'edit' && i18n.t('Common:save')}
          </Button>{' '}
          <Button color="secondary" onClick={() => setUserInfoModalOpen(false)}>
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <>
      <Button
        className="mt-2 mb-2"
        style={{ float: 'right' }}
        onClick={addUser}
      >
        <FaPlus />
        {' ' + i18n.t('EventAccount:addUser')}
      </Button>
      <Table>
        <thead>
          <tr>
            <th>{i18n.t('EventAccount:username')}</th>
            <th>{i18n.t('EventAccount:password')}</th>
            <th>{i18n.t('EventAccount:canDoOrderFulfillments')}</th>
            <th>{i18n.t('Common:edit')}</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={4}>{i18n.t('Common:loading')}...</td>
            </tr>
          )}
          {error && (
            <tr>
              <td colSpan={4}>{i18n.t('Common:error')}</td>
            </tr>
          )}
          {[...users]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((u) => (
              <tr key={u.id}>
                <td>
                  {CanAccessUserData(viewer) && (
                    <Link to={'/users/' + u.id}>{u.name}</Link>
                  )}
                  {!CanAccessUserData(viewer) && <span>{u.name}</span>}
                </td>
                <td>{u.password}</td>
                <td>
                  {u.can_do_order_fulfillments
                    ? i18n.t('Common:yes')
                    : i18n.t('Common:no')}
                </td>
                <td>
                  <Button onClick={() => editUser(u)}>
                    <FaEdit />
                  </Button>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {userInfoModal()}
    </>
  );
};

export default EventAccountUserTable;
